import { USERS_NOT_IN_CALIBRATION_GROUP_VALUE } from "@apps/reporting/components/use-calibration-group-picker";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import AppLink from "@components/link/link";
import ProgressBar from "@components/progress-bar/progress-bar";
import {
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableContainer,
  TableHeadCell,
  TableHeadRow,
} from "@components/table/table";
import { classNames } from "@helpers/css";

type CalibrationGroup = {
  id: number;
  name: string;
  users: { id: number }[];
  approvers: { name: string }[];
  team?: { title: string } | null;
  madeFromDepartment: boolean;
  participantCount: number;
  calibratedCount: number;
};

// The department is null when it was deleted after the group was made.
const getMadeFrom = (group: CalibrationGroup) =>
  group.team?.title ??
  (group.madeFromDepartment ? "A deleted department" : "By hand");

/**
 * The first page of the Calibration tab: one row per group the viewer may
 * see, with its approvers and how far its people are calibrated. The server
 * counts the review's people: the report rows skip the viewer. A click on a
 * row opens the group.
 */
const CalibrationGroupList = ({
  calibrationGroups,
  peopleWithoutGroupCount,
  getGroupUrl,
  onOpenGroup,
  onCreateGroup,
}: {
  calibrationGroups: readonly CalibrationGroup[];
  // Null: no row for the people of the review in no group.
  peopleWithoutGroupCount: number | null;
  // The value is a group id, or USERS_NOT_IN_CALIBRATION_GROUP_VALUE.
  getGroupUrl: (value: number) => string;
  onOpenGroup: (value: number) => void;
  // Without it, no "Create group" button.
  onCreateGroup?: () => void;
}) => {
  const renderRow = (
    key: number,
    name: string,
    cells: [string, string, React.ReactNode, string],
  ) => (
    <TableBodyRow
      key={key}
      className="group cursor-pointer"
      onClick={() => onOpenGroup(key)}
    >
      <TableBodyCell className="group-hover:bg-gray-50">
        {/* A link, so a cmd-click opens the group in a new tab. The link
            navigates itself: after a deploy it loads the new app build. */}
        <AppLink
          to={getGroupUrl(key)}
          className="font-medium text-gray-800"
          onClick={(event) => event.stopPropagation()}
        >
          {name}
        </AppLink>
      </TableBodyCell>
      {cells.map((cell, index) => (
        <TableBodyCell
          key={index}
          className={classNames(
            "group-hover:bg-gray-50",
            index === 1 && "tabular-nums",
          )}
        >
          {cell}
        </TableBodyCell>
      ))}
    </TableBodyRow>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-medium text-gray-800">Groups</h2>
        {onCreateGroup && (
          <Button
            theme={buttonTheme.primary}
            text="Create group"
            onClick={onCreateGroup}
          />
        )}
      </div>
      <TableContainer>
        <Table>
          <TableHeadRow>
            <TableHeadCell>Group</TableHeadCell>
            <TableHeadCell>Approvers</TableHeadCell>
            <TableHeadCell>People</TableHeadCell>
            <TableHeadCell>Calibrated</TableHeadCell>
            <TableHeadCell>Made from</TableHeadCell>
          </TableHeadRow>
          <TableBody>
            {calibrationGroups.map((group) =>
              renderRow(group.id, group.name, [
                group.approvers.map(({ name }) => name).join(", ") || "—",
                `${group.participantCount}`,
                <ProgressBar
                  key="calibrated"
                  progress={
                    group.participantCount
                      ? (group.calibratedCount / group.participantCount) * 100
                      : 0
                  }
                  label={`${group.calibratedCount} of ${group.participantCount}`}
                />,
                getMadeFrom(group),
              ]),
            )}
            {peopleWithoutGroupCount !== null &&
              renderRow(
                USERS_NOT_IN_CALIBRATION_GROUP_VALUE,
                "Not in a group",
                [
                  "—",
                  `${peopleWithoutGroupCount}`,
                  <span
                    key="calibrated"
                    className={
                      peopleWithoutGroupCount ? "text-amber-700" : undefined
                    }
                  >
                    {peopleWithoutGroupCount
                      ? `${peopleWithoutGroupCount} need a group`
                      : "Everyone is in a group"}
                  </span>,
                  "—",
                ],
              )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default CalibrationGroupList;
